import { useEffect, useState } from "react";

import { Box, Button, Divider, FormControl, FormErrorMessage, FormLabel, HStack, Icon, Input, Text, Textarea, useDisclosure, useToast } from "@chakra-ui/react"
import { Field, Form, Formik } from "formik"
import { useLocation, useNavigate } from "react-router-dom"
import { HttpStatusCode } from "axios";
import { FaEye } from "react-icons/fa";

import { COLORS, DURATION_TOAST } from "@/common/constants";
import ModalConfirmation from "@/components/Modals/ModalConfirmation";
import SelectCountry from "@/components/Selects/SelectCountry";
import SelectApprovalStatus from "@/components/Selects/SelectApprovalStatus";
import { useAppSelector } from "@/redux/hooks";
import { RootState } from "@/redux/store";
import { reviewShipOwnerSchema } from "@/schemas/reviewShipOwnerSchema";
import { getShipOwnerById, updateStatusShipOperator } from "@/services/shipOwnerService";
import { downloadFile } from "@/services/downloadService";
import Loader from "@/components/Layouts/Loader";

const ReviewShipOwner = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const { isOpen, onOpen, onClose } = useDisclosure();
    const name = useAppSelector((state: RootState) => state.account.name);
    const toast = useToast();

    const { id, returnPath } = location.state || {};

    const [dataDetail, setDataDetail] = useState(null) as any;
    const [dataEdit, setDataEdit] = useState(null) as any;
    const [isLoading, setIsLoading] = useState(true);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [title, setTitle] = useState("");
    const [confirmationText, setConfirmationTest] = useState("");
    const [confirmationTitleButton, setConfirmationTitleButton] = useState("");

    const handleCancel = () => {
        onOpen();
        setTitle("Cancel to Update Status?");
        setConfirmationTest("Are you sure you want to cancel?");
        setConfirmationTitleButton("Yes");
    }

    const handleSubmit = async (data: any) => {
        setTitle("Update Status Ship Operator?");
        setConfirmationTest("Are you sure you want to update status?");
        setConfirmationTitleButton("Update Status Ship Operator");
        onOpen();
        setDataEdit(data);
        setIsSubmitting(true);
    }

    const generateData = () => {
        const obj = {
            shipOwnerId: id,
            note: dataEdit.note,
            registerStatus: parseInt(dataEdit.status),
            modifiedBy: name || "System",
        };
        return obj;
    }

    const handleConfirm = async () => {
        onClose();

        if (title === "Cancel to Update Status?") {
            navigate(returnPath || '/manage-ship-operator');
            return;
        }

        const obj = generateData();
        const response = await updateStatusShipOperator(obj) as any;
        if (response.status === HttpStatusCode.Ok) {
            setIsSubmitting(false);
            toast({
                title: 'Ship operator updated.',
                description: "We've updated ship operator status for you.",
                status: 'success',
                duration: DURATION_TOAST,
                isClosable: true,
            });
            setTimeout(() => {
                navigate(returnPath || '/manage-ship-operator')
            }, 1000)
        } else {
            setIsSubmitting(false);
            toast({
                title: 'Ship operator failed to update the status.',
                description: "We unabled to update ship operator status for you.",
                status: 'error',
                duration: DURATION_TOAST,
                isClosable: true,
            });
        }
    }

    const handleConfirmToCancel = () => {
        setIsSubmitting(false);
        onClose();
    }

    const handleDownload = async (id: string) => {
			await downloadFile(id);
    }

    const getDataDetail = async () => {
        setIsLoading(true);
        try {
            const response = await getShipOwnerById(id) as any;
            if (response) {
                setDataDetail(response.data);
            }
        } finally {
            setIsLoading(false);
        }
    }

    useEffect(() => {
        if (id) {
            getDataDetail();
        } else {
            setIsLoading(false);
        }
    }, [id])

    return (
        <>
            <ModalConfirmation isOpen={isOpen} onClose={onClose} title={title} handleCancel={handleConfirmToCancel} handleConfirm={handleConfirm} confirmationText={confirmationText} confirmButtonText={confirmationTitleButton} />
            {isLoading ? <Loader /> : <div className="form-container">
                <Formik
                    initialValues={{
                        onBehalfCompany: dataDetail?.onBehalfCompany ?? '',
                        address: dataDetail?.address ?? '',
                        country: dataDetail?.country ?? '',
                        contactPerson: dataDetail?.contactPerson ?? '',
                        email: dataDetail?.email ?? '',
                        alternateEmail: dataDetail?.alternateEmail ?? '',
                        phone: dataDetail?.phone ?? '',
                        status: dataDetail?.status === 1 ? '2' : dataDetail?.status.toString(),
                        note: dataDetail?.note ?? ''
                    }}
                    validationSchema={reviewShipOwnerSchema}
                    onSubmit={(values) => {
                        handleSubmit(values);
                    }}
                    enableReinitialize={true}
                >
                    {(props) => (
                        <Form>
                            <Field name='onBehalfCompany'>
                                {({ field, form }: any) => (
                                    <FormControl isInvalid={form.errors.onBehalfCompany && form.touched.onBehalfCompany} style={{ marginBottom: 15 }}>
                                        <div className="form-field-container">
                                            <FormLabel className="form-label">On Behalf Company<span style={{ color: 'red' }}>*</span></FormLabel>
                                            <div className="form-input-container">
                                                <Input {...field} placeholder='On Behalf Company' className="form-input" disabled />
                                                <FormErrorMessage>{form.errors.onBehalfCompany}</FormErrorMessage>
                                            </div>
                                        </div>
                                    </FormControl>
                                )}
                            </Field>
                            <Field name='address'>
                                {({ field, form }: any) => (
                                    <FormControl isInvalid={form.errors.address && form.touched.address} style={{ marginBottom: 15 }}>
                                        <div className="form-field-container">
                                            <FormLabel className="form-label">Address<span style={{ color: 'red' }}>*</span></FormLabel>
                                            <div className="form-input-container">
                                                <Textarea {...field} placeholder='Address' className="form-input" disabled />
                                                <FormErrorMessage>{form.errors.address}</FormErrorMessage>
                                            </div>
                                        </div>
                                    </FormControl>
                                )}
                            </Field>
                            <Field name='country'>
                                {({ form }: any) => (
                                    <FormControl isInvalid={form.errors.country && form.touched.country} style={{ marginBottom: 15 }}>
                                        <div className="form-field-container">
                                            <FormLabel className="form-label">Country<span style={{ color: 'red' }}>*</span></FormLabel>
                                            <div className="form-input-container">
                                                <SelectCountry name='country' handleChange={({ value }: any) => props.setFieldValue('country', value)} value={props.values.country} defaultValue={props.values.country} isDisabled />
                                                <FormErrorMessage>{form.errors.country}</FormErrorMessage>
                                            </div>
                                        </div>
                                    </FormControl>
                                )}
                            </Field>
                            <Field name='contactPerson'>
                                {({ field, form }: any) => (
                                    <FormControl isInvalid={form.errors.contactPerson && form.touched.contactPerson} style={{ marginBottom: 15 }}>
                                        <div className="form-field-container">
                                            <FormLabel className="form-label">Contact Person<span style={{ color: 'red' }}>*</span></FormLabel>
                                            <div className="form-input-container">
                                                <Input {...field} placeholder='Contact Person' className="form-input" disabled />
                                                <FormErrorMessage>{form.errors.contactPerson}</FormErrorMessage>
                                            </div>
                                        </div>
                                    </FormControl>
                                )}
                            </Field>
                            <Field name='email'>
                                {({ field, form }: any) => (
                                    <FormControl isInvalid={form.errors.email && form.touched.email} style={{ marginBottom: 15 }}>
                                        <div className="form-field-container">
                                            <FormLabel className="form-label">Email<span style={{ color: 'red' }}>*</span></FormLabel>
                                            <div className="form-input-container">
                                                <Input {...field} placeholder='Email' className="form-input" disabled />
                                                <FormErrorMessage>{form.errors.email}</FormErrorMessage>
                                            </div>
                                        </div>
                                    </FormControl>
                                )}
                            </Field>
                            <Field name='alternateEmail'>
                                {({ field, form }: any) => (
                                    <FormControl isInvalid={form.errors.alternateEmail && form.touched.alternateEmail} style={{ marginBottom: 15 }}>
                                        <div className="form-field-container">
                                            <FormLabel className="form-label">Alternative Email</FormLabel>
                                            <div className="form-input-container">
                                                <Input {...field} placeholder='Alternative Email' className="form-input" disabled />
                                                <FormErrorMessage>{form.errors.alternateEmail}</FormErrorMessage>
                                            </div>
                                        </div>
                                    </FormControl>
                                )}
                            </Field>
                            <Field name='phone'>
                                {({ field, form }: any) => (
                                    <FormControl isInvalid={form.errors.phone && form.touched.phone} style={{ marginBottom: 15 }}>
                                        <div className="form-field-container">
                                            <FormLabel className="form-label">Phone Number</FormLabel>
                                            <div className="form-input-container">
                                                <Input {...field} placeholder='Phone Number' className="form-input" disabled />
                                                <FormErrorMessage>{form.errors.phone}</FormErrorMessage>
                                            </div>
                                        </div>
                                    </FormControl>
                                )}
                            </Field>
                            <Field name='doc'>
                                {({ form }: any) => (
                                    <FormControl isInvalid={form.errors.cor && form.submitCount} style={{ marginBottom: 15 }}>
                                        <div className="form-field-container">
                                            <FormLabel className="form-label">DOC<span style={{ color: 'red' }}>*</span></FormLabel>
                                            <div className="form-input-container">
                                                <div style={{ display: 'flex', alignItems: 'center' }}>
                                                    <Box
                                                        borderWidth="1px"
                                                        borderRadius="md"
                                                        padding={4}
                                                        backgroundColor={dataDetail?.documents ? "green.50" : "yellow.50"}
                                                        borderColor={dataDetail?.documents ? "green.200" : "yellow.200"}
                                                        width="100%"
                                                    >
                                                        <HStack
                                                            borderWidth="1px"
                                                            borderRadius="md"
                                                            padding={3}
                                                            backgroundColor="white"
                                                            justifyContent="space-between"
                                                            width="100%"
                                                        >
                                                            <HStack>
                                                                <Text>{dataDetail?.documents ? 'COR File' : 'No file uploaded'}</Text>
                                                            </HStack>
                                                                <HStack cursor='pointer' onClick={() => handleDownload(dataDetail?.documents)}>
                                                                {dataDetail?.documents && <Icon as={FaEye} />}
                                                                <Text>{dataDetail?.documents ? 'Download Document' : ''}</Text>
                                                            </HStack>
                                                        </HStack>
                                                    </Box>
                                                    </div>
                                                </div>
                                            <FormErrorMessage>{form.errors.doc}</FormErrorMessage>
                                        </div>
                                        
                                    </FormControl>
                                )}
                            </Field>

                            <Field name='status'>
                                {({ form }: any) => (
                                    <FormControl isInvalid={form.errors.status && form.touched.status} style={{ marginBottom: 15, alignItems: 'center' }}>
                                        <div className="form-field-container">
                                            <FormLabel className="form-label">
                                                Status<span style={{ color: 'red' }}>*</span>
                                            </FormLabel>
                                            <div className="form-input-container">
                                                <SelectApprovalStatus name='status' handleChange={({ value }: any) => props.setFieldValue('status', value)} value={props.values.status} hasError={form.errors.status && form.touched.status} defaultValue={props.values.status} />
                                                <FormErrorMessage>{form.errors.status}</FormErrorMessage>
                                            </div>
                                        </div>
                                    </FormControl>
                                )}
                            </Field>
                            {props.values.status === "4" &&
                                <Field name='note'>
                                    {({ field, form }: any) => (
                                        <FormControl isInvalid={form.errors.note && form.touched.note} style={{ marginBottom: 15 }}>
                                            <div className="form-field-container">
                                                <FormLabel className="form-label">Note<span style={{ color: 'red' }}>*</span></FormLabel>
                                                <div className="form-input-container">
                                                    <Textarea {...field} placeholder='Note' className="form-input" isInvalid={form.errors.note && form.touched.note} />
                                                    <FormErrorMessage>{form.errors.note}</FormErrorMessage>
                                                </div>
                                            </div>
                                        </FormControl>
                                    )}
                                </Field>
                            }
                            <Divider />
                            <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                                <Button
                                    mt={4}
                                    bgColor='white'
                                    borderColor='#D0D5DD'
                                    isLoading={isSubmitting}
                                    marginRight={5}
                                    onClick={handleCancel}
                                >
                                    Cancel
                                </Button>
                                <Button
                                    mt={4}
                                    bgColor={COLORS.BUTTON.BACKGROUND}
                                    color={COLORS.BUTTON.FOREGROUND}
                                    isLoading={isSubmitting}
                                    type='submit'
                                >
                                    Update
                                </Button>
                            </div>
                        </Form>
                    )}
                </Formik>
            </div>}
        </>
    )
}

export default ReviewShipOwner;
